import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { api } from '../lib/api';
import { useSite } from '../context/SiteContext';
import ProductCard from '../components/ProductCard';
import SEO from '../components/SEO';
import HeroSlider from '../components/HeroSlider';
import { sanitizeHtml } from '../lib/sanitize';

export default function Home() {
    const { settings } = useSite();
    const [featured, setFeatured] = useState([]);
    const [collections, setCollections] = useState([]);

    useEffect(() => {
        api.get('/products', { params: { featured: true } }).then((r) => setFeatured(r.data.slice(0, 3))).catch(() => {});
        api.get('/collections').then((r) => setCollections(r.data.filter((c) => c.featured).slice(0, 3))).catch(() => {});
    }, []);

    const base = (settings?.site_url || 'https://byanfal.com').replace(/\/$/, '');
    const orgLd = {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: settings?.brand_name || 'by anfal',
        url: base,
        logo: settings?.logo_url || '',
        sameAs: Object.values(settings?.social || {}).filter(Boolean),
    };
    const siteLd = {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: settings?.brand_name || 'by anfal',
        url: base,
        potentialAction: {
            '@type': 'SearchAction',
            target: `${base}/shop?q={search_term_string}`,
            'query-input': 'required name=search_term_string',
        },
    };

    return (
        <div data-testid="home-page">
            <SEO canonical="/" jsonLd={[orgLd, siteLd]} />
            {/* HERO SLIDER */}
            <HeroSlider settings={settings} />

            {/* FEATURED PRODUCTS */}
            <section className="max-w-[1300px] mx-auto px-4 sm:px-6 lg:px-10 py-12 lg:py-16" data-testid="featured-products-section">
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                    className="flex flex-col items-center text-center mb-8"
                >
                    <p className="eyebrow" style={{ color: 'var(--brand-burgundy)' }}>The Edit</p>
                    <h2 className="font-serif text-4xl md:text-5xl mt-2 shine-text">Featured Pieces</h2>
                    <hr className="divider-burgundy mt-5" />
                </motion.div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
                    {featured.map((p, i) => (
                        <ProductCard key={p.id} product={p} index={i} />
                    ))}
                </div>
                {featured.length > 0 && (
                    <div className="mt-10 flex justify-center">
                        <Link to="/shop" data-testid="home-shop-all-link" className="btn-outline-burgundy px-6 py-3 rounded-xl text-xs tracking-[0.22em] uppercase">
                            Shop All
                        </Link>
                    </div>
                )}
            </section>

            {/* OUR STORY */}
            <section className="max-w-[1300px] mx-auto px-4 sm:px-6 lg:px-10 py-12 lg:py-16" data-testid="our-story-section">
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                    className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start"
                >
                    <div className="lg:col-span-5">
                        <p className="eyebrow" style={{ color: 'var(--brand-burgundy)' }}>About</p>
                        <h2 className="font-serif text-5xl md:text-6xl mt-3 leading-[0.95] shine-text">
                            {settings?.story_heading || 'Our Story'}
                        </h2>
                        <hr className="divider-burgundy mt-6" />
                    </div>
                    <div className="lg:col-span-7">
                        <div className="prose-luxury text-base md:text-lg leading-[1.85]" style={{ color: 'var(--ink-muted)' }} dangerouslySetInnerHTML={{ __html: sanitizeHtml(settings?.story_body) }} />
                        <Link
                            to={settings?.story_link_href || '/about'}
                            data-testid="our-story-learn-more-link"
                            className="mt-7 inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase hover:gap-3 transition-all duration-200"
                            style={{ color: 'var(--brand-burgundy)' }}
                        >
                            {settings?.story_link_label || 'LEARN MORE'} <span>&rsaquo;</span>
                        </Link>
                    </div>
                </motion.div>
            </section>

            {/* COLLECTIONS PREVIEW */}
            {collections.length > 0 && (
                <section className="max-w-[1300px] mx-auto px-4 sm:px-6 lg:px-10 py-12 lg:py-16" data-testid="collections-preview-section">
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                        className="flex flex-col items-center text-center mb-8"
                    >
                        <p className="eyebrow" style={{ color: 'var(--brand-burgundy)' }}>Lookbooks</p>
                        <h2 className="font-serif text-4xl md:text-5xl mt-2 shine-text">Explore Collections</h2>
                        <hr className="divider-burgundy mt-5" />
                    </motion.div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-7">
                        {collections.map((c, i) => (
                            <Link
                                key={c.id}
                                to={`/collections/${c.slug}`}
                                data-testid={`home-collection-card-${c.slug}`}
                                className="group relative overflow-hidden rounded-2xl aspect-[3/4] block fade-in"
                                style={{ animationDelay: `${i * 80}ms` }}
                            >
                                {c.cover_image && (
                                    <img src={c.cover_image} alt={c.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]" />
                                )}
                                <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,0.55) 100%)' }} />
                                <div className="absolute inset-x-0 bottom-0 p-6 text-white">
                                    <p className="eyebrow text-white/80">Collection</p>
                                    <h3 className="font-serif text-2xl md:text-3xl mt-1">{c.name}</h3>
                                </div>
                            </Link>
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
}
